import ProfilePhoto, { Width } from "../../components/profilePhoto"
import React from "react"
import tw from "twin.macro"
import { EmojiReactions, getEmojiForReaction } from "."
import {
  EmojiReaction,
  Platform,
  ProfileOnGetProfileFragment,
  QuestionDetailFragment,
} from "../../graphql/generated/graphql"
import { Routes } from "../../routes"
import Link from "next/link"
import { ReactComponent as DiscordIcon } from "../../assets/icons/discord.svg"
import { ReactComponent as YouTubeIcon } from "../../assets/icons/youTube.svg"
import { ReactComponent as InstagramIcon } from "../../assets/icons/instagram.svg"

interface ReactionProps {
  emoji: string
  Reactor: ProfileOnGetProfileFragment
}

const Reaction = ({ emoji, Reactor }: ReactionProps) => {
  const { profilePictureUrl, username, name, verified } = Reactor
  return (
    <li tw="py-2 flex items-center justify-between">
      <Link href={{ pathname: Routes.Profile, query: { username: username } }} passHref>
        <div tw="flex space-x-2 items-center hover:(cursor-pointer)">
          <div tw="inline-block">
            <ProfilePhoto profilePictureUrl={profilePictureUrl} width={Width.W30} showVerified={verified} />
          </div>
          <div tw="flex flex-col">
            <p tw="text-xs font-bold text-base-4">{username}</p>
            <p tw="text-xs text-base-4">{name}</p>
          </div>
        </div>
      </Link>
      <div tw="text-xl">{getEmojiForReaction(emoji)}</div>
    </li>
  )
}

interface OffPlatformReactionProps {
  total: number
  platform: Platform
}

const otherPlatformFaces = ({ total }: OffPlatformReactionProps) => {
  const totalFaces = total > 3 ? 3 : total
  const faces = Array(totalFaces)
  for (let i = 0; i < totalFaces; i++) {
    faces.push(
      <ProfilePhoto
        profilePictureUrl={`https://${
          process.env.NEXT_PUBLIC_AWS_PROFILE_PICTURE_BUCKET
        }.s3.amazonaws.com/default_${Math.floor(Math.random() * 5)}.png`}
        width={Width.W30}
      />,
    )
  }
  return faces
}

const OffPlatformReaction = ({ total, platform }: OffPlatformReactionProps) => {
  const copy = `reaction${total > 1 ? `s` : ``} on `
  const otherFaces = otherPlatformFaces({ total, platform })
  return (
    <li tw="py-2 flex items-center justify-between border-b-2 border-base-2 pb-2">
      <div tw="flex -space-x-4 items-center">{otherFaces}</div>
      <div tw="flex flex-row">
        <p tw="text-xs text-base-4 font-bold">{total}</p>
        <p tw="pl-1 text-xs text-base-4">
          {copy}
          <span tw="capitalize">{platform.toLowerCase()}</span>
        </p>
      </div>
      <div tw="text-xl">
        {platform === Platform.Youtube && <YouTubeIcon />}
        {platform === Platform.Discord && <DiscordIcon />}
        {platform === Platform.Instagram && <InstagramIcon />}
      </div>
    </li>
  )
}

interface AllReactionsProps {
  question: QuestionDetailFragment
}

export const AllReactions = ({ question }: AllReactionsProps) => {
  const { reactions, SourceComment: sourceComment, discordReactionCount } = question
  let reactionLikeCount = null
  let reactionPlatform = null
  if (sourceComment) {
    const { likeCount, platform } = sourceComment
    reactionLikeCount = likeCount
    reactionPlatform = platform
  } else {
    reactionLikeCount = discordReactionCount ?? 0
    reactionPlatform = reactionLikeCount > 0 ? Platform.Discord : null
  }

  return (
    <div tw="space-y-2" css={[tw`mobile-lg:(w-[260px])`]}>
      <h4 tw="text-center">Reactions</h4>
      <ul tw="divide-y-2 divide-base-2">
        {reactionLikeCount > 0 && reactionPlatform && (
          <OffPlatformReaction total={reactionLikeCount} platform={reactionPlatform} />
        )}
      </ul>
      <ul tw="divide-y-2 divide-base-2">
        {reactions.map((item: ReactionProps) => {
          const { emoji, Reactor } = item
          return <Reaction key={Reactor.id} emoji={emoji} Reactor={Reactor} />
        })}
      </ul>
    </div>
  )
}
